import { Box, ButtonBase, createStyles, Divider, Icon, makeStyles, Theme, Typography } from '@material-ui/core';
import cn from 'classnames';
import * as React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { transformInfoBank } from '../../containers/NegotiationDebts/transformInfoBank';
import { portalRootUrl } from '../../containers/Portal/portalRootUrl';
import { BankType, NegotiationState, NegotiationType, Normative026, OutDebt } from '../../generated/graphql';
import { getEntityLabel } from '../../utils/financialEntitiesList';
import { getDebtConfigByType } from '../../utils/getDebtConfigByType';
import { getJcapBalance } from '../../utils/getJcapBalance';
import { getNegBooleans } from '../../utils/getNegBooleans';
import { getNegotiationOptionLabel } from '../../utils/getNegotiationOptionLabel';
import thousandSeparator from '../../utils/thousandSeparator';
import { useGetProductNumberDef } from '../../utils/useGetProductNumberDef';
import Btn from '../Btn/Btn';
import ACPKDebtsSummaryTable from './ACPKDebtsSummaryTable';

interface DebtCardProps {
  debt: OutDebt;
}

interface NegotiationFooterProps {
  negType: NegotiationType,
  state: NegotiationState,
  bankType: BankType,
  normative026?: Normative026,
}

const DebtCard: React.FC<DebtCardProps> = ({ debt }) => {
  const { balance: balanceRaw, id: debtId, daysBehind, productNumber: productNumberRaw } = debt;
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  const classes = useStyles();

  const { bank, type } = transformInfoBank(debt);

  const upToDate = false;
  const dataTest = `cy-debtCard-${type}-${productNumberRaw?.slice(-4) || debtId}`;

  const debtData = getDebtConfigByType(type);

  const {
    isVirtualDebt, hasNeg, opType, negState, isRappiPay, isJcap, isNoBank,
  } = getNegBooleans(debt);
  const debtLink = `${portalRootUrl()}/deuda/${debt.negotiation?.extra.virtualId || debtId}`;

  const showProductNumber = !isRappiPay;

  const productNumberDef = useGetProductNumberDef(debt);
  const productNumber = debt.bank === BankType.BancoDeBogota ? productNumberDef : productNumberRaw;

  const balance = isJcap ? getJcapBalance(debt) : (balanceRaw || 0);

  const hideBank = isJcap && isNoBank;
  const entityLabel = hideBank ? '' : getEntityLabel(bank, debt.brand);
  const headingLabel = debtData?.iconName || (hideBank ? '' : entityLabel[0]);
  const virtualHeader = (
    <>
      <Icon className={cn(classes.img, 'mb-2')}>
        {headingLabel}
      </Icon>
      <Typography className={cn(classes.bank)}>{ debtData?.label }</Typography>
      <Typography className={cn(classes.bank, 'mb-1')}>{ entityLabel }</Typography>
      <ACPKDebtsSummaryTable debt={debt} dataTest={`${dataTest}-summaryTable`} />
    </>
  );

  const header = (
    <>
      <Icon className={cn(classes.img, 'mb-2')}>
        { debtData?.iconName || entityLabel}
      </Icon>
      <Typography className={classes.bank}>{entityLabel}</Typography>
      <Typography className={classes.type}>
        {debtData?.label || bank}
        {' '}
        {showProductNumber ? productNumber : ''}
      </Typography>
      <Divider className="my-1" style={{ width: '100%' }} />
    </>
  );

  const virtualFooter = (
    <Btn
      className="mt-1 mb-n1 px-4"
      component={RouterLink}
      to={debtLink}
    >
      Unificar
    </Btn>
  );

  const footer = (
    <>
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        data-test={`${dataTest}-saldo`}
      >
        <Typography className={classes.text}>Saldo</Typography>
        <Typography className={classes.amount}>
          ${thousandSeparator(balance, 'abs')}
        </Typography>
      </Box>
      {daysBehind !== 0 && (
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          data-test={`${dataTest}-mora`}
        >
          <Typography className={classes.text}>Días de mora</Typography>
          <Typography className={classes.amount}>{daysBehind} días</Typography>
        </Box>
      )}
    </>
  );

  const NegotiationFooter: React.FC<NegotiationFooterProps> = ({
    negType, state, bankType, normative026,
  }) => {
    const negLabel = getNegotiationOptionLabel(
      negType,
      bankType,
      normative026,
      debt.negotiation?.alternative,
      debt.brand,
    );
    const message: { [key in NegotiationState]: string } = {
      [NegotiationState.ConfirmedByUser]: 'Solicitud enviada',
      [NegotiationState.PrepaymentCompleted]: 'Solicitud enviada',
      [NegotiationState.SentToBank]: 'Solicitud enviada',
      [NegotiationState.ApprovedByBank]: 'Solicitud aprobada',
      [NegotiationState.RejectedByBank]: 'Solicitud rechazada',
      [NegotiationState.Expired]: 'Solicitud vencida',
      [NegotiationState.PaymentInProgress]: 'Solicitud enviada',
      [NegotiationState.PrepaymentIncomplete]: 'Solicitud rechazada',
    };
    return (
      <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center">
        <Typography className={classes.negTypeLabel} align="center">{message[state]}</Typography>
        <Typography className={classes.text} align="center">{negLabel}</Typography>
      </Box>
    );
  };

  const ParentComponent = isVirtualDebt && !hasNeg ? Box : ButtonBase;

  if (upToDate) {
    return (
      <Box data-test={dataTest} className={classes.root}>
        {isVirtualDebt ? virtualHeader : header}
        <Box display="flex" justifyContent="space-evenly" alignItems="center" style={{ width: '100%' }}>
          <Typography className={classes.textPaid}>Esta deuda está al día</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <ParentComponent
      data-test={dataTest}
      className={cn(classes.root, !(isVirtualDebt && !hasNeg) && classes.rootClickable)}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...(!(isVirtualDebt && !hasNeg)
        ? { component: RouterLink, to: debtLink }
        : {})
        }
    >
      {isVirtualDebt ? virtualHeader : header}
      <Box display="flex" justifyContent="space-evenly" alignItems="center" style={{ width: '100%' }}>
        {!hasNeg && (isVirtualDebt ? virtualFooter : footer)}
        {hasNeg && (
        <NegotiationFooter
          negType={opType!}
          state={negState}
          bankType={debt.bank}
          normative026={debt.extra?.normative026 ? debt.extra.normative026 : undefined}
        />
        )}
      </Box>
    </ParentComponent>
  );
};

const useStyles = makeStyles((theme: Theme) => createStyles({
  root: {
    width: '100%',
    height: '100%',
    padding: theme.spacing(3),
    boxSizing: 'border-box',
    boxShadow: theme.shadows[3],
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    alignItems: 'center',
    border: 'solid 1px transparent',
    transition: 'all .4s',
    borderRadius: theme.spacing(0.5),
  },
  rootClickable: {
    cursor: 'pointer',
    '&:hover': {
      boxShadow: theme.shadows[5],
      border: `solid 1px ${theme.palette.primary.main}`,
    },
  },
  img: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    color: theme.palette.indigo.main,
    fontSize: '2.8em',
  },
  bank: {
    ...theme.typography.body1,
    color: theme.palette.indigo.main,
    fontWeight: 600,
    textAlign: 'center',
    textTransform: 'uppercase',
  },
  type: {
    ...theme.typography.small as any,
    color: theme.palette.text4.main,
  },
  text: {
    ...theme.typography.small as any,
    color: theme.palette.text4.main,
  },
  amount: {
    ...theme.typography.body1,
    color: theme.palette.lavender.main,
    fontWeight: 600,
  },
  negTypeLabel: {
    ...theme.typography.body1,
    color: theme.palette.primary.main,
    fontWeight: 600,
  },
  textPaid: {
    ...theme.typography.body1,
    color: theme.palette.primary.main,
    fontWeight: 600,
  },
}));

export default DebtCard;
